import { Film, MessageSquare, Clapperboard, Video } from "lucide-react";
import TestimonialCard from "@/components/TestimonialCard";
import ServiceContactForm from "@/components/ServiceContactForm";

const steps = [
  {
    step: "First Step",
    title: "Discuss & Gather",
    icon: MessageSquare,
    description:
      "Before we or anyone else picks up a piece of equipment for photo or video, planning and discussing project scope should always be done first. Things like budgets, practicality, etc. need to be discussed. Nothing worse than diving into a project without the budget to finish, or simply not having the proper resources to pull it off.",
  },
  {
    step: "Second Step",
    title: "Pre-Production",
    icon: Clapperboard,
    description:
      "Now it's time to really hone in on and narrow down the specifics. We already know the direction we want to head — now it's time to go over everything needed for production. If there's anything we feel we don't need or need to add, this is the time to nail it down. It's time to figure out shoot locations, casting, etc.",
  },
  {
    step: "Third Step",
    title: "Shoot the Film",
    icon: Video,
    description:
      "Time to shoot! Before getting ahead of the program it's essential to keep in mind all of the details discussed prior to production. Remember there's a budget and not sticking to a script can be detrimental to the success of your film project. All parties must communicate to ensure things go smoothly throughout the production phase.",
  },
];

const shooters = [
  {
    name: "Cedric Searcy",
    image: "https://bookmystudiospace.com/wp-content/uploads/2016/05/Personnel-Cedric-700x853.jpg",
  },
  {
    name: "Tabitha Suggs-Searcy",
    image: "https://bookmystudiospace.com/wp-content/uploads/2022/04/tab-editorial-1-700x935.jpg",
  },
  {
    name: "The Pentaxx Guy",
    image: "https://bookmystudiospace.com/wp-content/uploads/2022/04/pentaxx-guy-102-700x935.jpg",
  },
  {
    name: "Gary FRLN Jackson",
    image: "https://bookmystudiospace.com/wp-content/uploads/2016/05/gary-700x853.jpg",
  },
  {
    name: "Rachael Banks",
    image: "https://bookmystudiospace.com/wp-content/uploads/2016/05/rach-banks-web-1-700x853.jpg",
  },
  {
    name: "Deontae Holloway",
    image: "https://bookmystudiospace.com/wp-content/uploads/2016/05/deontae-700x853.jpg",
  },
];

const ProductionServicePage = () => (
  <div className="pt-20">
    {/* Hero */}
    <section className="py-20 lg:py-28 text-center">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-6xl font-display tracking-widest mb-4">
          FILM &amp; PRODUCTION SERVICES
        </h1>
        <p className="text-muted-foreground text-lg font-body max-w-2xl mx-auto">
          ADVERTISE – FILMING – YOU NAME IT
        </p>
      </div>
    </section>

    {/* Description */}
    <section className="pb-16">
      <div className="container mx-auto px-4 max-w-3xl">
        <div className="bg-panel border border-border p-8 text-center">
          <Film className="w-12 h-12 text-primary mx-auto mb-6" />
          <p className="text-foreground/80 font-body leading-relaxed">
            Let us help you tell your story with motion. We have a network of
            Filmmakers and Cinematographers that will help bring your video
            projects to life! Rest assured that we're not referring to the
            watered down same ole style of filmmaking. We want to sit down with
            you and make sure that we outline the scope of your film project in
            detail to ensure that we help deliver your final production the way
            you envisioned it! Let's work today!
          </p>
        </div>
      </div>
    </section>

    {/* 3-Step Process */}
    <section className="pb-20">
      <div className="container mx-auto px-4 max-w-5xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div
              key={s.title}
              className="bg-panel border border-border p-8 text-center"
            >
              <s.icon className="w-10 h-10 text-primary mx-auto mb-3" />
              <p className="text-xs text-muted-foreground font-body uppercase tracking-widest mb-1">
                {s.step}
              </p>
              <h2 className="text-xl font-display tracking-wider mb-4">
                {s.title}
              </h2>
              <p className="text-sm text-foreground/80 font-body leading-relaxed">
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Meet the Shooters */}
    <section className="pb-20">
      <div className="container mx-auto px-4">
        <h2 className="text-2xl md:text-4xl font-display tracking-widest text-center mb-4">
          SOME OF THE SHOOTERS
        </h2>
        <p className="text-muted-foreground font-body text-center mb-12">
          The folks who get the job done.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 max-w-6xl mx-auto">
          {shooters.map((p) => (
            <div key={p.name} className="group text-center">
              <div className="aspect-[3/4] overflow-hidden border border-border mb-3">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
              </div>
              <p className="text-sm font-display tracking-wider">{p.name}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Testimonial */}
    <TestimonialCard
      quote="Cedric is definitely the guy to go to for capturing those special moments! Highly recommended! Cedric is awesome in what he does and works diligently to make sure everything goes smooth and perfect."
      name="Angie Perez"
      title="Satisfied Client"
      image="https://bookmystudiospace.com/wp-content/uploads/2022/04/tab-editorial-1-700x935.jpg"
    />

    {/* Contact Form */}
    <ServiceContactForm defaultService="Film & Video Production" />
  </div>
);

export default ProductionServicePage;
